import React, { useState } from "react";
import api from "../../api";

// Lucide Icons
import {
  Mail,
  Send,
  KeyRound,
  AlertCircle,
  Loader2,
  Shield,
  ArrowRight,
  CheckCircle,
  LockKeyhole,
  Smartphone,
} from "lucide-react";
import "./SendOTPMail.css";
import { useAlert } from "react-alert";

function SendOTPMail({ match }) {
  const alert = useAlert();

  const [email, setEmail] = useState("");
  const [loader, setLoader] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    try {
      if (!email) {
        setError("Please enter an email address");
        return;
      }

      setError("");
      setLoader(true);

      const payload = { email };
      const userData = await api.sendOTPMail(payload);

      if (userData.success) {
        setIsSent(true);
        setEmail("");
        alert.success(userData.message);
      } else {
        setError(userData?.message || "Something went wrong, email not sent");
      }
    } catch (error) {
      console.log(error);
      setError("Something went wrong, email not sent");
    } finally {
      setLoader(false);
    }
  };

  const handleReset = () => {
    setIsSent(false);
    setEmail("");
    setError("");
  };

  return (
    <>
      <div className="send-otp-mail-container">
        <div className="send-otp-mail-content">
          {/* Header Section */}
          <div className="send-otp-mail-header">
            <div className="send-otp-mail-header-icon">
              <LockKeyhole size={40} />
            </div>
            <h1 className="send-otp-mail-title">Send OTP Verification</h1>
            <p className="send-otp-mail-subtitle">
              Send one-time passwords to users for secure verification
            </p>
          </div>

          {/* Main Content */}
          <div className="send-otp-mail-card">
            {isSent ? (
              /* Success State */
              <div className="send-otp-mail-success">
                <div className="send-otp-mail-success-icon">
                  <CheckCircle size={48} />
                </div>
                <h2 className="send-otp-mail-success-title">OTP Email Sent</h2>
                <p className="send-otp-mail-success-message">
                  One-time password has been successfully sent to the user. The
                  OTP will expire shortly for security purposes.
                </p>
                <div className="send-otp-mail-success-actions">
                  <button
                    className="send-otp-mail-success-button"
                    onClick={handleReset}
                  >
                    <ArrowRight
                      size={18}
                      style={{ transform: "rotate(180deg)" }}
                    />
                    Send Another OTP
                  </button>
                </div>
              </div>
            ) : (
              /* Form Section */
              <div className="send-otp-mail-form">
                <div className="send-otp-mail-input-section">
                  <div className="send-otp-mail-input-header">
                    <Mail className="send-otp-mail-input-header-icon" />
                    <label className="send-otp-mail-input-label">
                      User Email Address
                    </label>
                  </div>

                  <div className="send-otp-mail-input-wrapper">
                    <Mail className="send-otp-mail-input-icon" size={20} />
                    <input
                      type="email"
                      className="send-otp-mail-input"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError("");
                      }}
                      placeholder="Enter user's email address for OTP..."
                      onKeyPress={(e) => e.key === "Enter" && handleSubmit()}
                    />
                  </div>

                  {error && (
                    <div className="send-otp-mail-error">
                      <AlertCircle size={16} />
                      <span>{error}</span>
                    </div>
                  )}

                  <div className="send-otp-mail-input-hint">
                    <KeyRound size={14} />
                    <span>
                      A one-time password will be sent for verification
                    </span>
                  </div>
                </div>

                <button
                  onClick={handleSubmit}
                  className="send-otp-mail-submit-button"
                  disabled={loader}
                >
                  {loader ? (
                    <>
                      <Loader2 className="send-otp-mail-loader" size={20} />
                      Sending OTP Email...
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      Send OTP Email
                    </>
                  )}
                </button>

                <div className="send-otp-mail-security-note">
                  <Shield size={16} />
                  <div>
                    <strong>Security Notice:</strong> OTPs are time-sensitive
                    and valid for a limited duration only.
                  </div>
                </div>
              </div>
            )}

            {/* Stats/Info Section */}
            <div className="send-otp-mail-info">
              <div className="send-otp-mail-info-item">
                <div className="send-otp-mail-info-icon">
                  <LockKeyhole size={18} />
                </div>
                <div className="send-otp-mail-info-content">
                  <span className="send-otp-mail-info-label">Secure OTP</span>
                  <span className="send-otp-mail-info-value">6-Digit Code</span>
                </div>
              </div>
              <div className="send-otp-mail-info-item">
                <div className="send-otp-mail-info-icon">
                  <Smartphone size={18} />
                </div>
                <div className="send-otp-mail-info-content">
                  <span className="send-otp-mail-info-label">
                    Quick Delivery
                  </span>
                  <span className="send-otp-mail-info-value">
                    Instant Email
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default SendOTPMail;
